import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/student/BottomNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Brain, Divide, Plus, Minus, X, Zap, Grid3x3, Filter, Target, Percent, Sparkles,
  Trophy, Loader2, BookOpen, Play, CheckCircle2,
} from "lucide-react";

const ICONS = { Brain, Divide, Plus, Minus, X, Zap, Grid3x3, Filter, Target, Percent, Sparkles };

const HUBS = {
  mental: {
    title: "Mental Maths", tagline: "Speed arithmetic in your head", icon: Brain,
    color: "#8B5CF6", lip: "#6D28D9", bg: "#F5F3FF", border: "#DDD6FE", text: "#6D28D9",
    intro: "No pencil, no calculator - just your brain! Pick a game, choose a level and beat your best score.",
  },
  vedic: {
    title: "Vedic Maths", tagline: "Ancient shortcuts & tricks", icon: Divide,
    color: "#EC4899", lip: "#BE185D", bg: "#FDF2F8", border: "#FBCFE8", text: "#BE185D",
    intro: "Learn a sutra (trick) in one minute, then practise it until it becomes magic. Every answer comes with the trick explained.",
  },
};

const LessonDialog = ({ mode, hub, onClose, onPractice }) => (
  <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md" data-testid="vedic-lesson-dialog">
      {mode && (
        <>
          <DialogHeader><DialogTitle className="font-['Fredoka'] text-xl" style={{ color: hub.text }}>{mode.name}</DialogTitle></DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{mode.lesson.sutra}</p>
          <p className="text-sm text-slate-700 leading-relaxed" data-testid="vedic-lesson-rule">{mode.lesson.rule}</p>
          <ol className="space-y-2 mt-1">
            {mode.lesson.steps.map((s, i) => (
              <li key={i} className="flex gap-3 items-start rounded-xl p-2.5 text-sm font-semibold text-slate-800 animate-in fade-in slide-in-from-left-2"
                style={{ background: hub.bg, animationDelay: `${i * 120}ms`, animationFillMode: "both" }}>
                <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0" style={{ background: hub.color }}>{i + 1}</span>{s}
              </li>
            ))}
          </ol>
          <Button className="w-full h-12 rounded-2xl text-white mt-2" style={{ background: hub.color }} onClick={() => onPractice(mode)} data-testid="vedic-lesson-practice">
            <Play className="w-4 h-4 mr-2" /> Practise this trick
          </Button>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function MathsHubPage({ hub: hubId }) {
  const hub = HUBS[hubId];
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modes, setModes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    Promise.all([axios.get(`${API}/brain-games/modes?hub=${hubId}`, { headers }), axios.get(`${API}/brain-games/stats`, { headers })])
      .then(([m, s]) => { setModes(m.data.modes || []); setStats(s.data.stats || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hubId, token]);

  const played = modes.filter((m) => stats[m.id]).length;
  const HubIcon = hub.icon;
  const play = (m) => navigate(`/brain-gym/${m.id}`);

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-10 font-['Lexend']" data-testid={`${hubId}-hub-page`}>
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/student")} className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors" aria-label="Back" data-testid={`${hubId}-hub-back`}>
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: hub.color }}><HubIcon className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="text-base font-bold text-slate-800 font-['Fredoka'] leading-tight">{hub.title}</h1>
            <p className="text-[11px] text-slate-400 leading-tight">{hub.tagline}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        <div className="rounded-2xl p-5 mb-5 border-2 border-b-4" style={{ background: hub.bg, borderColor: hub.border, borderBottomColor: hub.lip }} data-testid={`${hubId}-hub-intro`}>
          <p className="text-sm text-slate-700">{hub.intro}</p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2.5 rounded-full bg-white overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${modes.length ? (played / modes.length) * 100 : 0}%`, background: hub.color }} />
            </div>
            <span className="text-xs font-bold" style={{ color: hub.text }} data-testid={`${hubId}-hub-progress`}>{played}/{modes.length} tried</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {modes.map((m, idx) => {
              const Icon = ICONS[m.icon] || Zap;
              const best = stats[m.id];
              return (
                <div key={m.id} className="rounded-2xl p-4 border-2 border-b-4 bg-white flex flex-col transition-transform duration-200 hover:-translate-y-1 hover:shadow-md animate-in fade-in slide-in-from-bottom-2"
                  style={{ borderColor: hub.border, borderBottomColor: hub.lip, animationDelay: `${idx * 60}ms`, animationFillMode: "both" }} data-testid={`hub-mode-${m.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: hub.color }}><Icon className="w-5 h-5 text-white" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold leading-tight font-['Fredoka']" style={{ color: hub.text }}>{m.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                    </div>
                    {best && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" aria-label="Played" />}
                  </div>
                  <div className="mt-3 text-[11px] font-semibold flex items-center gap-1" style={{ color: best ? hub.text : "#94a3b8" }} data-testid={`hub-best-${m.id}`}>
                    {best ? <><Trophy className="w-3 h-3" /> Best {best.best_score} · {best.best_accuracy}% · {best.plays} plays</> : "Not played yet"}
                  </div>
                  <div className="mt-3 flex gap-2">
                    {m.lesson && (
                      <Button variant="outline" size="sm" className="flex-1 rounded-xl" onClick={() => setLesson(m)} data-testid={`hub-learn-${m.id}`}>
                        <BookOpen className="w-4 h-4 mr-1" /> Learn trick
                      </Button>
                    )}
                    <Button size="sm" className="flex-1 rounded-xl text-white" style={{ background: hub.color }} onClick={() => play(m)} data-testid={`hub-play-${m.id}`}>
                      <Play className="w-4 h-4 mr-1" /> Play
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <LessonDialog mode={lesson} hub={hub} onClose={() => setLesson(null)} onPractice={(m) => { setLesson(null); play(m); }} />
      <BottomNav />
    </div>
  );
}
